import { PropsWithChildren } from "react";

import { cn } from "@aircall/ds";
import { useTranslation } from "react-i18next";

import { EvaluationQuestionSummaryRatingProps } from ".";

function getColorClassFromValue(value: string): string {
  switch (value) {
    case "YES":
      return "text-success";
    case "NO":
      return "text-destructive";
    case "EXCELLENT":
      return "text-success";
    case "GOOD":
      return "text-success";
    case "FAIR":
      return "text-warning";
    case "POOR":
      return "text-destructive";
    case "VERY_POOR":
      return "text-destructive";
    default:
      return "text-muted-foreground";
  }
}

function getLabelFromValue(value: string): string {
  switch (value) {
    case "YES":
      return "yes";
    case "NO":
      return "no";
    case "EXCELLENT":
      return "excellent";
    case "GOOD":
      return "good";
    case "FAIR":
      return "fair";
    case "POOR":
      return "poor";
    case "VERY_POOR":
      return "very_poor";
    default:
      return "";
  }
}

function Container<Value extends string>({
  children,
  disabled,
}: PropsWithChildren<
  Pick<EvaluationQuestionSummaryRatingProps<Value>, "disabled">
>) {
  return (
    <div
      className={cn(
        "flex min-w-[120px] shrink-0 flex-col items-center gap-0.5 rounded-lg border border-border p-3",
        disabled ? "cursor-not-allowed bg-muted" : "bg-card"
      )}
    >
      {children}
    </div>
  );
}

export function EvaluationQuestionSummaryRating<Value extends string>({
  value,
  maximumPoints,
  points,
  disabled,
}: EvaluationQuestionSummaryRatingProps<Value>) {
  const { t } = useTranslation();

  if (disabled) {
    return (
      <Container disabled={disabled}>
        <p className="text-xl font-bold text-muted-foreground">
          {t("not_applicable")}
        </p>
        <p className="text-xs font-normal text-muted-foreground">
          {t("evaluation.question_summary_points", {
            text: "--",
          })}
        </p>
      </Container>
    );
  }

  if (!value) {
    return null;
  }

  const colorClass = getColorClassFromValue(value);
  const label = getLabelFromValue(value);

  return (
    <Container>
      <p className={cn("text-xl font-bold", colorClass)}>{t(label)}</p>
      <p className="text-xs font-normal text-muted-foreground">
        {t("evaluation.question_summary_points", {
          text: `${points ?? "--"} / ${maximumPoints}`,
        })}
      </p>
    </Container>
  );
}
